import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import OrderLayout from '../../../layout/order-layout';
import { APP_API_URL } from '../../../utils';

const SOURCES = [
  { value: 'stock', label: 'Boutique' },
  { value: 'damaged', label: 'Cassés / gâtés' },
  { value: 'broken', label: 'Carreaux cassés' },
];

const sourceLabel = (source) => SOURCES.find((item) => item.value === source)?.label || source;

export default function AddDonation() {
  const navigate = useNavigate();
  const [source, setSource] = useState('stock');
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [beneficiaryType, setBeneficiaryType] = useState('association');
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/donations/catalog`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { source },
      });
      setCatalog(response.data.items || []);
    } catch (error) {
      console.error(error);
      setCatalog([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [source]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return catalog;
    return catalog.filter((item) =>
      [item.name, item.sku, item.category].filter(Boolean).join(' ').toLowerCase().includes(term)
    );
  }, [catalog, search]);

  const addItem = (item) => {
    setCart((prev) => {
      const existing = prev.find((line) => line.source === source && line.variantId === item.variant_id);
      if (existing) {
        if (existing.quantity + 1 > item.quantity) return prev;
        return prev.map((line) => (
          line.uniqueId === existing.uniqueId ? { ...line, quantity: line.quantity + 1 } : line
        ));
      }
      return [...prev, {
        uniqueId: `${source}-${item.variant_id}`,
        source,
        variantId: item.variant_id,
        name: item.name,
        unit: item.unit || '',
        max: item.quantity,
        quantity: 1,
      }];
    });
  };

  const canSave = beneficiaryName.trim().length >= 2 && cart.length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${APP_API_URL}/donations`, {
        beneficiary_type: beneficiaryType,
        beneficiary_name: beneficiaryName.trim(),
        note,
        items: cart.map((line) => ({
          source: line.source,
          variant_id: line.variantId,
          quantity: line.quantity,
        })),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCart([]);
      await Swal.fire({
        icon: 'success',
        title: 'Don enregistré',
        text: 'Le stock est retiré. Aucune facture n’a été créée.',
        timer: 1600,
        showConfirmButton: false,
      });
      navigate('/admin/dons');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        text: error.response?.data?.message || 'Le don n’a pas pu être enregistré.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <OrderLayout>
      <div className="duo-caisse-layout">
        <div className="duo-caisse-products-col">
          <div className="duo-caisse-products-header">
            <div className="duo-caisse-toolbar">
              <Link to="/admin/dons" className="duo-btn duo-btn--ghost duo-btn--sm">
                <i className="bi bi-list-ul" /> Historique
              </Link>
            </div>
            <h2 className="duo-caisse-title">Nouveau don</h2>
            <p className="duo-caisse-subtitle">Comme une vente, sans facture. Le stock choisi est retiré tout de suite.</p>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {SOURCES.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`duo-btn duo-btn--sm${source === item.value ? ' duo-btn--primary' : ' duo-btn--ghost'}`}
                  onClick={() => { setSource(item.value); setSearch(''); }}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <input
              type="search"
              className="duo-input mt-2"
              placeholder="Rechercher un article…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="duo-caisse-products-scroll">
            {catalogLoading ? (
              <p className="text-center text-muted py-5">Chargement du stock…</p>
            ) : visible.length === 0 ? (
              <div className="text-center py-5">
                <img src="/assets/img/no-product.png" width={220} height={220} alt="Aucun article" />
                <p className="mt-3 text-muted">Aucun article disponible dans ce stock.</p>
              </div>
            ) : (
              <div className="duo-product-grid">
                {visible.map((item) => (
                  <div
                    key={item.variant_id}
                    role="button"
                    tabIndex={0}
                    className="duo-product-card"
                    onClick={() => addItem(item)}
                    onKeyDown={(e) => e.key === 'Enter' && addItem(item)}
                  >
                    <p className="duo-product-card__name">{item.name}</p>
                    <p className="duo-product-card__sku">{item.category || item.sku}</p>
                    <div className="duo-product-card__meta">
                      <span className="duo-product-card__stock">{item.quantity} {item.unit || ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="duo-caisse-cart-col">
          <div className="duo-cart-panel">
            <div className="duo-cart-panel__inner">
              <div className="duo-cart-panel__head">
                <h2>Don <span className="duo-cart-count">{cart.length}</span></h2>
                <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => setCart([])}>Vider</button>
              </div>
              <div className="mb-3">
                <label className="duo-form-label">Bénéficiaire</label>
                <div className="d-flex gap-2 mb-2">
                  <button
                    type="button"
                    className={`duo-btn duo-btn--sm${beneficiaryType === 'association' ? ' duo-btn--primary' : ' duo-btn--ghost'}`}
                    onClick={() => setBeneficiaryType('association')}
                  >
                    Association / ONG
                  </button>
                  <button
                    type="button"
                    className={`duo-btn duo-btn--sm${beneficiaryType === 'personne' ? ' duo-btn--primary' : ' duo-btn--ghost'}`}
                    onClick={() => setBeneficiaryType('personne')}
                  >
                    Personne
                  </button>
                </div>
                <input
                  className="duo-input"
                  value={beneficiaryName}
                  onChange={(e) => setBeneficiaryName(e.target.value)}
                  placeholder={beneficiaryType === 'association' ? "Nom de l'association" : 'Nom de la personne'}
                />
              </div>
              {cart.length === 0 ? (
                <p className="small text-muted">Ajoutez des articles depuis la gauche.</p>
              ) : cart.map((line) => (
                <div className="duo-cart-item" key={line.uniqueId}>
                  <div className="d-flex justify-content-between">
                    <span className="duo-cart-item__name">{line.name}</span>
                    <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => setCart(cart.filter((item) => item.uniqueId !== line.uniqueId))}>
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                  <p className="small text-muted mb-1">{sourceLabel(line.source)}</p>
                  <label className="duo-form-label">Quantité {line.unit}</label>
                  <input
                    type="number"
                    className="duo-input"
                    min="0.01"
                    step="0.01"
                    max={line.max}
                    value={line.quantity}
                    onChange={(e) => {
                      let next = parseFloat(e.target.value);
                      if (!next || next < 0.01) next = 0.01;
                      if (next > line.max) next = line.max;
                      setCart(cart.map((item) => item.uniqueId === line.uniqueId ? { ...item, quantity: next } : item));
                    }}
                  />
                </div>
              ))}
              <div className="mt-3">
                <label className="duo-form-label">Note</label>
                <textarea className="duo-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optionnel" />
              </div>
              <button type="button" className="duo-btn duo-btn--primary w-100 mt-3" disabled={!canSave} onClick={handleSubmit}>
                {saving ? 'Enregistrement…' : 'Enregistrer le don'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </OrderLayout>
  );
}
